import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Color, Width } from '@figure/frontlib-gui';
import SpinnerImg from '../assets/images/spinner.svg';
import PhoneImg from '../assets/images/phone.svg';

const StyledContainer = styled.div`
  position: relative;
`;

const StyledMediaContainer = styled.div`
  position: relative;
  min-height: 388px;

  audio {
    visibility: hidden;
  }
`;

const absoluteMedia = (minimize) => {
  if (!minimize) return '';

  return `
    position: fixed;
    top: 15px;
    right: 15px;
    width: 50px;
    height: 50px;
    border-radius: 4px;
    overflow: hidden;

    @media print, screen and (min-width: ${Width.MD}px) {
      top: 50px;
      right: 50px;
      width: 80px;
      height: 80px;
    }
  `;
};

const StyledRemoteMedia = styled.div`
  video {
    width: 100%;
    z-index: 1010;
    transition: 0.2s;
    ${(props) => absoluteMedia(props.minimize)};
  }
`;

const StyledLoader = styled.div`
  position: absolute;
  background-color: ${Color.BACKGROUND_GREY};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  h3 {
    text-transform: uppercase;
    color: #808080;
  }
`;

const StyledSpinner = styled.span`
  display: block;
  width: 100%;
  height: 50px;
  margin: 0 auto;
  background: url(${SpinnerImg}) center no-repeat;
  flex-shrink: 0;
`;

const StyledLocalMedia = styled.div`
  > video {
    position: absolute;
    bottom: 15px;
    right: 15px;
    background-color: ${Color.DARK_GREY};
    width: 50px;
    height: 50px;
    border-radius: 8px;
    object-fit: cover;
    z-index: 2;

    &:nth-of-type(2) {
      right: 80px;
    }
    &:nth-of-type(3) {
      right: 145px;
    }

    @media print, screen and (min-width: ${Width.MD}px) {
      width: ${(props) => props.remoteWidth}px;
      height: ${(props) => props.remoteWidth}px;

      &:nth-of-type(2) {
        bottom: ${(props) => props.remoteWidth + 30}px;
        right: 15px;
      }

      &:nth-of-type(3) {
        bottom: ${(props) => props.remoteWidth * 2 + 45}px;
        right: 15px;
      }
    }

    @media print, screen and (max-width: ${Width.SM}px) {
      &:nth-of-type(2) {
        bottom: 75px;
        right: 15px;
      }

      &:nth-of-type(3) {
        bottom: 135px;
        right: 15px;
      }
    }
  }
`;

const StyledDisconnectButton = styled.button`
  width: 64px;
  height: 64px;
  border-radius: 100%;
  border: none;
  background: ${(props) => (props.disabled ? Color.DARK_GREY : Color.RED)} url(${PhoneImg})
    no-repeat center;
  position: absolute;
  bottom: 15px;
  left: 50%;
  opacity: ${(props) => (props.disabled ? '0.7' : '1')};
  transform: translateX(-50%);
  z-index: 2;
`;

const TwilioVideo = ({
  room,
  onParticipantConnect,
  onParticipantDisconnect,
  position,
  minimize,
  continuable,
  onDisconnect,
}) => {
  const [applicantVideoConnected, setApplicantVideoConnected] = useState(false);
  const [error, setError] = useState('');
  const localMedia = useRef(null);
  const remoteMedia = useRef(null);
  const remoteParticipants = useRef(0);

  useEffect(() => {
    if (room) {
      roomJoined(room);
    }
  }, [room]);

  const roomJoined = (room) => {
    // connect local video
    participantConnected(room.localParticipant, true);

    room.participants.forEach((p) => participantConnected(p, false));

    room.on('participantConnected', (p) => {
      console.log(`Joining ${p}`); // eslint-disable-line no-console
      participantConnected(p);
    });

    room.on('participantDisconnected', participantDisconnected);

    // handle disconnection
    room.once('disconnected', () => {
      participantDisconnected(room.localParticipant, true);
      room.participants.forEach(participantDisconnected);
    });

    room.localParticipant.on('trackPublicationFailed', (error, localTrack) => {
      // eslint-disable-next-line no-console
      console.warn(`Failed to publish LocalTrack ${localTrack.name}: ${error.message}`);
      setError(error.message);
    });

    room.on('trackSubscriptionFailed', (error, remoteTrackPublication, remoteParticipant) => {
      // eslint-disable-next-line no-console
      console.warn(
        `Failed to subscribe to RemoteTrack ${remoteTrackPublication.trackName} from RemoteParticipant ${remoteParticipant.identity}: ${error.message}`
      );
      setError(error);
    });
  };

  const attachTrack = (track, container) => {
    container.appendChild(track.attach());
  };

  const attachTracks = (tracks, container) =>
    tracks.filter((t) => t.track).forEach((t) => attachTrack(t.track, container));

  const detachTracks = (tracks) => {
    // https://github.com/twilio/twilio-video.js/issues/294
    // TODO: remove temp hack once webkit fixes track issues
    const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);

    tracks
      .filter((t) => t.track)
      .forEach((t) => {
        if (isSafari) {
          t.track._attachments.forEach((e) => e.remove()); // eslint-disable-line no-underscore-dangle
        } else {
          t.track.detach().forEach((e) => e.remove());
        }
      });
  };

  // A new RemoteTrack was published to the Room.
  const trackPublished = (publication, container) => {
    if (publication.isSubscribed && publication.track) {
      attachTrack(publication.track, container);
    }
    publication.on('subscribed', (track) => {
      attachTrack(track, container);
    });
    publication.on('unsubscribed', (track) => {
      track.detach().forEach((element) => {
        element.remove();
      });
    });
  };

  // A RemoteTrack was unpublished from the Room.
  const trackUnpublished = (publication) => {
    detachTracks([publication.track]);
  };

  const participantConnected = (participant, local = false) => {
    console.log(`Participant ${participant} ${local} connected`); // eslint-disable-line no-console

    // we only have one remote participant as the primary video
    const container =
      local || remoteParticipants.current > 0 ? remoteMedia.current : localMedia.current;
    const tracks = Array.from(participant.tracks.values());

    if (!local && !applicantVideoConnected) {
      if (tracks.filter((t) => t.kind === 'video').length > 0) {
        setApplicantVideoConnected(true);
      }
    }

    attachTracks(tracks, container);
    if (!local) {
      remoteParticipants.current += 1;
    }

    participant.tracks.forEach((publication) => {
      trackPublished(publication, container);
    });
    participant.on('trackPublished', (publication) => {
      trackPublished(publication, container);
    });
    participant.on('trackUnpublished', trackUnpublished);

    // notify parent of connection
    onParticipantConnect(participant);
  };

  const participantDisconnected = (participant, local = false) => {
    console.log(`Participant ${participant} disconnected`); // eslint-disable-line no-console

    detachTracks(Array.from(participant.tracks.values()));
    if (!local) {
      remoteParticipants.current -= 1;
      room.disconnect();
    }

    // notify parent of disconnect
    onParticipantDisconnect(participant);
  };

  // https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number
  const ordinalSuffix = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;

    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  let message = '';
  if (error) {
    message = error;
  } else if (applicantVideoConnected && !remoteParticipants.current) {
    message = 'Notary has disconnected';
  }

  const connecting =
    position > 0
      ? `You are ${ordinalSuffix(position)} in line.`
      : 'Connecting to  a Customer Success Associate';

  // reduce size of small video
  const remoteWidth = remoteParticipants.current < 3 ? 130 : 100;

  return (
    <StyledContainer>
      <StyledLoader>
        {!applicantVideoConnected && (
          <h3>
            <StyledSpinner />
            {connecting}
          </h3>
        )}

        {message && <h3>{message}</h3>}
      </StyledLoader>

      <StyledMediaContainer>
        <StyledRemoteMedia className="fs-mask" ref={localMedia} minimize={minimize} />
        <StyledLocalMedia className="fs-mask" ref={remoteMedia} remoteWidth={remoteWidth} />
        <StyledDisconnectButton
          onClick={() => {
            if (continuable) {
              room?.disconnect();
              onDisconnect();
            }
          }}
          disabled={!continuable}
        />
      </StyledMediaContainer>
    </StyledContainer>
  );
};

TwilioVideo.propTypes = {
  room: PropTypes.shape({
    localParticipant: PropTypes.shape({
      on: PropTypes.func.isRequired,
    }),
    participants: PropTypes.arrayOf(PropTypes.shape({})),
    disconnect: PropTypes.func,
    on: PropTypes.func,
    once: PropTypes.func,
  }).isRequired,
  onParticipantConnect: PropTypes.func.isRequired,
  onParticipantDisconnect: PropTypes.func.isRequired,
  position: PropTypes.number.isRequired,
  minimize: PropTypes.bool.isRequired,
  continuable: PropTypes.bool.isRequired,
  onDisconnect: PropTypes.func.isRequired,
};

export default TwilioVideo;
